import {
  RevenueIntelligenceChatFacts,
  RevenueIntelligenceChatDataset,
  RevenueIntelligenceChatInsight,
  RevenueIntelligenceChatMetric,
  RevenueIntelligenceChatRecordSet,
  RevenueIntelligenceChatRegistrationValue,
  RevenueIntelligenceChatScope,
} from "../components/RevenueIntelligenceChatContext";
import { RevenueIntelligenceChatAccessPolicy } from "./revenue-intelligence-chat-access";

export type RevenueIntelligenceMetricContract = {
  metricId: string;
  label: string;
  aliases: string[];
  valueLabel: string;
  definition: string;
  includedRecords: string;
  dateField: string;
  valueBasis: string;
  currencyHandling: string;
  probabilityOrRiskBasis: string;
  sourceFields: string[];
  caveats?: string[];
};

export type RevenueIntelligenceAnalysisInsight = Omit<
  RevenueIntelligenceChatInsight,
  "requiredAccess" | "sourceHelper" | "topics"
>;

export type RevenueIntelligenceAnalysisRecordSet = RevenueIntelligenceChatRecordSet;

type AvailableChatContract = {
  availability: "available";
  datasets?: RevenueIntelligenceChatDataset[];
  facts?: RevenueIntelligenceChatFacts | null;
  insights: RevenueIntelligenceAnalysisInsight[];
  recordSets?: RevenueIntelligenceAnalysisRecordSet[];
  suggestedQuestions: string[];
  topics: string[];
};

type UnavailableChatContract = {
  availability: "unavailable";
  reason: string;
  requiredDependency: string;
};

export type RevenueIntelligenceAnalysisContract = {
  contractId: string;
  analysisLabel: string;
  accessPolicy: RevenueIntelligenceChatAccessPolicy;
  productLens: string;
  requiredAccess: string;
  sourceHelper: string;
  scope: RevenueIntelligenceChatScope;
  freshness?: string;
  metrics: RevenueIntelligenceMetricContract[];
  chat: AvailableChatContract | UnavailableChatContract;
};

export type RevenueIntelligenceAnalysisResult<TData> = {
  data: TData;
  contract: RevenueIntelligenceAnalysisContract;
};

function requireText(value: string, field: string) {
  if (!value.trim()) throw new Error(`Revenue Intelligence analysis contract requires ${field}.`);
}

export function validateRevenueIntelligenceAnalysisContract(
  contract: RevenueIntelligenceAnalysisContract,
) {
  requireText(contract.contractId, "contractId");
  requireText(contract.analysisLabel, "analysisLabel");
  requireText(contract.productLens, "productLens");
  requireText(contract.requiredAccess, "requiredAccess");
  requireText(contract.sourceHelper, "sourceHelper");
  requireText(contract.scope.pageLabel, "scope.pageLabel");
  requireText(contract.accessPolicy.companyId, "accessPolicy.companyId");
  if (!contract.accessPolicy.allowedRoles.length) {
    throw new Error(`Revenue Intelligence analysis contract ${contract.contractId} requires allowedRoles.`);
  }
  if (
    contract.accessPolicy.dataScope !== "company"
    && contract.accessPolicy.enforcement !== "server_filtered"
  ) {
    throw new Error(
      `Revenue Intelligence ${contract.accessPolicy.dataScope} scope requires server_filtered enforcement.`,
    );
  }

  const metricIds = new Set<string>();
  contract.metrics.forEach((metric) => {
    requireText(metric.metricId, "metrics[].metricId");
    if (metricIds.has(metric.metricId)) {
      throw new Error(`Duplicate Revenue Intelligence metricId: ${metric.metricId}.`);
    }
    metricIds.add(metric.metricId);
    requireText(metric.label, `${metric.metricId}.label`);
    requireText(metric.valueLabel, `${metric.metricId}.valueLabel`);
    requireText(metric.definition, `${metric.metricId}.definition`);
    requireText(metric.includedRecords, `${metric.metricId}.includedRecords`);
    requireText(metric.dateField, `${metric.metricId}.dateField`);
    requireText(metric.valueBasis, `${metric.metricId}.valueBasis`);
    requireText(metric.currencyHandling, `${metric.metricId}.currencyHandling`);
    requireText(metric.probabilityOrRiskBasis, `${metric.metricId}.probabilityOrRiskBasis`);
    if (!metric.sourceFields.length) {
      throw new Error(`Revenue Intelligence metric ${metric.metricId} requires sourceFields.`);
    }
  });

  if (contract.chat.availability === "unavailable") {
    requireText(contract.chat.reason, "chat.reason");
    requireText(contract.chat.requiredDependency, "chat.requiredDependency");
    return contract;
  }

  if (
    !contract.chat.insights.length
    && !contract.chat.facts
    && !contract.metrics.length
    && !contract.chat.recordSets?.length
  ) {
    throw new Error(
      `Revenue Intelligence analysis contract ${contract.contractId} exposes no approved chat data.`,
    );
  }
  if (!contract.chat.topics.length) {
    throw new Error(`Revenue Intelligence analysis contract ${contract.contractId} requires chat topics.`);
  }

  const insightIds = new Set<string>();
  contract.chat.insights.forEach((insight) => {
    requireText(insight.insightId, "chat.insights[].insightId");
    if (insightIds.has(insight.insightId)) {
      throw new Error(`Duplicate Revenue Intelligence insightId: ${insight.insightId}.`);
    }
    insightIds.add(insight.insightId);
    requireText(insight.title, `${insight.insightId}.title`);
    requireText(insight.body, `${insight.insightId}.body`);
    requireText(insight.evidence, `${insight.insightId}.evidence`);
    if (insight.signal) {
      requireText(
        insight.signal.identity.signalId,
        `${insight.insightId}.signal.identity.signalId`,
      );
      requireText(
        insight.signal.identity.sourceFingerprint,
        `${insight.insightId}.signal.identity.sourceFingerprint`,
      );
    }
  });

  const recordSetIds = new Set<string>();
  contract.chat.recordSets?.forEach((recordSet) => {
    requireText(recordSet.recordSetId, "chat.recordSets[].recordSetId");
    if (recordSetIds.has(recordSet.recordSetId)) {
      throw new Error(`Duplicate Revenue Intelligence recordSetId: ${recordSet.recordSetId}.`);
    }
    recordSetIds.add(recordSet.recordSetId);
    requireText(recordSet.label, `${recordSet.recordSetId}.label`);
    requireText(recordSet.responseTitle, `${recordSet.recordSetId}.responseTitle`);
    requireText(recordSet.rankingDescription, `${recordSet.recordSetId}.rankingDescription`);
    requireText(recordSet.emptyMessage, `${recordSet.recordSetId}.emptyMessage`);
    requireText(recordSet.sourceHelper, `${recordSet.recordSetId}.sourceHelper`);
    if (!recordSet.aliases.length) {
      throw new Error(`Revenue Intelligence record set ${recordSet.recordSetId} requires aliases.`);
    }
    if (!recordSet.sourceFields.length) {
      throw new Error(`Revenue Intelligence record set ${recordSet.recordSetId} requires sourceFields.`);
    }
    if (!recordSet.fields.length) {
      throw new Error(`Revenue Intelligence record set ${recordSet.recordSetId} requires fields.`);
    }
    const recordFieldNames = new Set<string>();
    recordSet.fields.forEach((field) => {
      requireText(field.field, `${recordSet.recordSetId}.fields[].field`);
      requireText(field.label, `${recordSet.recordSetId}.fields[].label`);
      if (recordFieldNames.has(field.field)) {
        throw new Error(
          `Duplicate Revenue Intelligence record field ${field.field} in ${recordSet.recordSetId}.`,
        );
      }
      recordFieldNames.add(field.field);
    });
    if (!recordFieldNames.has(recordSet.defaultSort.field)) {
      throw new Error(
        `Revenue Intelligence record set ${recordSet.recordSetId} default sort field is unavailable.`,
      );
    }
    if (
      !recordSet.fields.find(field => field.field === recordSet.defaultSort.field)
        ?.sortable
    ) {
      throw new Error(
        `Revenue Intelligence record set ${recordSet.recordSetId} default sort field is not sortable.`,
      );
    }
    const recordIds = new Set<string>();
    recordSet.records.forEach((record) => {
      requireText(record.id, `${recordSet.recordSetId}.records[].id`);
      requireText(record.label, `${recordSet.recordSetId}.records[].label`);
      if (recordIds.has(record.id)) {
        throw new Error(
          `Duplicate Revenue Intelligence record id ${record.id} in ${recordSet.recordSetId}.`,
        );
      }
      recordIds.add(record.id);
      recordFieldNames.forEach((field) => {
        if (!(field in record.values)) {
          throw new Error(
            `Revenue Intelligence record ${record.id} in ${recordSet.recordSetId} requires ${field}.`,
          );
        }
      });
    });
  });

  const datasetIds = new Set<string>();
  contract.chat.datasets?.forEach((dataset) => {
    requireText(dataset.datasetId, "chat.datasets[].datasetId");
    if (datasetIds.has(dataset.datasetId)) {
      throw new Error(`Duplicate Revenue Intelligence datasetId: ${dataset.datasetId}.`);
    }
    datasetIds.add(dataset.datasetId);
    requireText(dataset.label, `${dataset.datasetId}.label`);
    requireText(dataset.description, `${dataset.datasetId}.description`);
    requireText(dataset.sourceHelper, `${dataset.datasetId}.sourceHelper`);
    if (!dataset.sourceFields.length) {
      throw new Error(`Revenue Intelligence dataset ${dataset.datasetId} requires sourceFields.`);
    }
    if (!dataset.dimensions.length || !dataset.measures.length) {
      throw new Error(
        `Revenue Intelligence dataset ${dataset.datasetId} requires dimensions and measures.`,
      );
    }
    const dimensionFields = new Set(dataset.dimensions.map(field => field.field));
    const measureFields = new Set(dataset.measures.map(field => field.field));
    const xFieldIsAllowed = dimensionFields.has(dataset.defaultChart.xField)
      || (
        dataset.defaultChart.type === "scatter"
        && measureFields.has(dataset.defaultChart.xField)
      );
    if (!xFieldIsAllowed) {
      throw new Error(
        `Revenue Intelligence dataset ${dataset.datasetId} default xField is not approved.`,
      );
    }
    if (!dataset.defaultChart.yFields.every(field => measureFields.has(field))) {
      throw new Error(
        `Revenue Intelligence dataset ${dataset.datasetId} default yFields are not measures.`,
      );
    }
    if (!dataset.allowedChartTypes.includes(dataset.defaultChart.type)) {
      throw new Error(
        `Revenue Intelligence dataset ${dataset.datasetId} default chart type is not allowed.`,
      );
    }
    dataset.rows.forEach((row) => {
      [...dimensionFields, ...measureFields].forEach((field) => {
        if (!(field in row)) {
          throw new Error(
            `Revenue Intelligence dataset ${dataset.datasetId} row requires ${field}.`,
          );
        }
      });
    });
  });

  return contract;
}

export function defineRevenueIntelligenceAnalysisContract(
  contract: RevenueIntelligenceAnalysisContract,
) {
  return validateRevenueIntelligenceAnalysisContract(contract);
}

export function createRevenueIntelligenceAnalysisResult<TData>(
  data: TData,
  contract: RevenueIntelligenceAnalysisContract,
): RevenueIntelligenceAnalysisResult<TData> {
  return {
    data,
    contract: defineRevenueIntelligenceAnalysisContract(contract),
  };
}

export function getChatRegistrationFromAnalysisContract(
  contract: RevenueIntelligenceAnalysisContract,
): RevenueIntelligenceChatRegistrationValue | null {
  validateRevenueIntelligenceAnalysisContract(contract);
  if (contract.chat.availability === "unavailable") return null;

  const metrics: RevenueIntelligenceChatMetric[] = contract.metrics.map(metric => ({
    ...metric,
    sourceHelper: contract.sourceHelper,
    sourceLabel: contract.analysisLabel,
  }));
  const insights: RevenueIntelligenceChatInsight[] = contract.chat.insights.map(insight => ({
    ...insight,
    requiredAccess: contract.requiredAccess,
    sourceHelper: contract.sourceHelper,
    sourceLabel: contract.analysisLabel,
    topics: contract.chat.availability === "available" ? contract.chat.topics : [],
    signal: insight.signal ?? {
      availableActions: [
        "explain" as const,
        "view_evidence" as const,
        ...(insight.recommendedAction ? ["recommend" as const] : []),
      ],
      identity: {
        signalId: `analysis:${contract.contractId}:${insight.insightId}`,
        sourceFingerprint: `${contract.contractId}:${insight.insightId}`,
      },
      lifecycleState: "active" as const,
      linkedRecordIds: [],
      priorityDimensions: {
        novelty: "known" as const,
      },
    },
  }));
  const recordSets: RevenueIntelligenceChatRecordSet[] = (
    contract.chat.recordSets ?? []
  ).map(recordSet => ({
    ...recordSet,
    sourceLabel: recordSet.sourceLabel ?? contract.analysisLabel,
  }));

  return {
    accessPolicy: contract.accessPolicy,
    contractId: contract.contractId,
    facts: contract.chat.facts,
    datasets: contract.chat.datasets ?? [],
    insights,
    metrics,
    recordSets,
    scope: contract.scope,
    scopeKey: JSON.stringify(contract.scope),
    suggestedQuestions: contract.chat.suggestedQuestions,
    topics: contract.chat.topics,
  };
}
